'use client';

import { E2EWalletName } from '@jet-lab/e2e-react-adapter';
import { useEffect, useRef } from 'react';
import useHandleSelectWalletProvider from 'src/hooks/useHandleSelectWalletProvider';
import useWalletContext from 'src/hooks/useWalletContext';

const isE2eWalletParamSet = () =>
	typeof window !== 'undefined' &&
	new URLSearchParams(window.location.search).get('e2eWallet') === 'true';

/**
 * Dev-only: lets automated browser tests (Playwright) connect a real signing
 * wallet by appending `?e2eWallet=true` to any route.
 *
 * The keypair comes from `TEST_PRIVATE_KEY` in `ui/.env.local`, which
 * `next.config.js` exposes as `NEXT_PUBLIC_E2E_TEST_WALLET_SECRET` in
 * non-production builds only. Setting `window.useE2eWallet` makes AppWrapper
 * add the E2E adapter to the wallet list (polled every 1s); once it appears we
 * run the normal wallet selection flow against it.
 */
const useConnectE2eWallet = () => {
	const walletContext = useWalletContext();
	const { handleSelectProvider } = useHandleSelectWalletProvider();
	const hasAttemptedConnect = useRef(false);

	useEffect(() => {
		if (
			process.env.NODE_ENV === 'production' ||
			hasAttemptedConnect.current ||
			!isE2eWalletParamSet()
		) {
			return;
		}

		window.useE2eWallet = true;

		const e2eWalletIsAvailable = walletContext.wallets.some(
			(wallet) => wallet.adapter.name === E2EWalletName
		);

		if (!e2eWalletIsAvailable) return;

		hasAttemptedConnect.current = true;
		handleSelectProvider(E2EWalletName);
	}, [walletContext.wallets, handleSelectProvider]);
};

export default useConnectE2eWallet;
